/**
 * KaTeX math rendering component.
 *
 * Wraps a block of text/HTML and processes all math delimiters
 * ($...$, $$...$$, \(...\), \[...\]) via katex/contrib/auto-render.
 */
import { useEffect, useRef, type ReactNode, type RefObject } from "react";
import "katex/dist/katex.min.css";
import renderMathInElement from "katex/contrib/auto-render";

interface MathProps {
  children: ReactNode;
  /** Additional CSS class */
  className?: string;
  /** Render as a specific HTML tag (default: div) */
  as?: keyof JSX.IntrinsicElements;
}

const DELIMITERS = [
  { left: "$$", right: "$$", display: true },
  { left: "$", right: "$", display: false },
  { left: "\\(", right: "\\)", display: false },
  { left: "\\[", right: "\\]", display: true },
];

export function Math({ children, className, as: Tag = "div" }: MathProps) {
  const ref = useRef<HTMLElement>(null);

  useEffect(() => {
    if (ref.current) {
      renderMathInElement(ref.current, {
        delimiters: DELIMITERS,
        throwOnError: false,
        trust: false,
      });
    }
  });

  return (
    // @ts-expect-error dynamic tag
    <Tag ref={ref} className={className}>
      {children}
    </Tag>
  );
}

/**
 * Convert markdown-style tables in text to HTML tables.
 *
 * Handles:
 * 1. Single-line inline tables (as stored in YAML):
 *    "Some text: | H1 | H2 | |---|---| | V1 | V2 | | V3 | V4 |"
 * 2. Multi-line markdown tables (lines starting with |)
 */
function markdownTablesToHtml(text: string): string {
  const lines = text.split("\n");
  const result: string[] = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];
    const trimmed = line.trim();

    // Single-line inline table takes priority: contains separator pattern
    // within the same line (e.g. "| H1 | H2 | |---|---| | V1 | V2 |")
    if (hasSeparatorPattern(trimmed)) {
      result.push(convertSingleLineTable(trimmed));
      i++;
      continue;
    }

    // Multi-line table: consecutive lines starting with |
    if (trimmed.startsWith("|") && trimmed.endsWith("|") && trimmed.split("|").length >= 3) {
      const tableLines: string[] = [];
      while (i < lines.length) {
        const tl = lines[i].trim();
        if (tl.startsWith("|") && tl.endsWith("|")) {
          tableLines.push(tl);
          i++;
        } else {
          break;
        }
      }
      if (tableLines.length >= 2) {
        result.push(buildMultiLineTable(tableLines));
      } else {
        result.push(tableLines.join("\n"));
      }
      continue;
    }

    result.push(line);
    i++;
  }

  return result.join("\n");
}

/**
 * Check if a line contains a table separator pattern: |---|---|
 */
function hasSeparatorPattern(line: string): boolean {
  return /\|\s*[-:]+\s*(\|\s*[-:]+\s*)+\|/.test(line);
}

/**
 * Convert a single-line table to HTML.
 * Format: "prefix text | H1 | H2 | |---|---| | V1 | V2 | | V3 | V4 | suffix text"
 */
function convertSingleLineTable(line: string): string {
  // Find the separator pattern
  const sepRegex = /\|\s*[-:]+\s*(\|\s*[-:]+\s*)+\|/;
  const sepMatch = line.match(sepRegex);
  if (!sepMatch || sepMatch.index === undefined) return line;

  const sepStart = sepMatch.index;
  const sepEnd = sepStart + sepMatch[0].length;

  // Determine number of columns from the separator
  const sepCells = sepMatch[0].split("|").filter(c => c.trim().length > 0);
  const colCount = sepCells.length;

  // Extract header from before the separator
  const beforeSep = line.substring(0, sepStart);
  // Extract data from after the separator
  const afterSep = line.substring(sepEnd);

  // Find all pipe positions in beforeSep, from right to left
  const pipePositions: number[] = [];
  for (let i = beforeSep.length - 1; i >= 0; i--) {
    if (beforeSep[i] === "|") pipePositions.push(i);
  }

  // Header row has colCount+1 pipes: | H1 | H2 | ... | Hn |
  // The opening pipe of the header is at pipePositions[colCount]
  let prefix = "";
  let headerCells: string[] = [];

  if (pipePositions.length >= colCount + 1) {
    const headerStartPos = pipePositions[colCount];
    prefix = beforeSep.substring(0, headerStartPos).trim();
    const headerPart = beforeSep.substring(headerStartPos).trim();
    headerCells = headerPart.split("|").map(c => c.trim()).filter(c => c.length > 0);
  } else {
    // Fallback: use all content before sep as header
    headerCells = beforeSep.split("|").map(c => c.trim()).filter(c => c.length > 0);
  }

  // Parse data rows from afterSep — detect trailing text after last complete row
  // Strategy: split on | but track positions to find where table data ends
  // A data row is bounded by pipes: | cell1 | cell2 | ... | cellN |
  // After the last row's closing pipe, remaining text is suffix.
  const dataRows: string[][] = [];
  let remaining = afterSep;

  // Parse rows by matching | cell1 | cell2 | ... | cellN | patterns
  let pattern = "^\\s*";
  for (let c = 0; c < colCount; c++) {
    pattern += "[|]\\s*([^|]*?)\\s*";
  }
  pattern += "[|]";
  const rowRegex = new RegExp(pattern);

  while (remaining.length > 0) {
    const rowMatch = remaining.match(rowRegex);
    if (rowMatch) {
      const cells: string[] = [];
      for (let c = 1; c <= colCount; c++) {
        cells.push(rowMatch[c]?.trim() || "");
      }
      dataRows.push(cells);
      remaining = remaining.substring(rowMatch[0].length);
    } else {
      break;
    }
  }

  const suffix = remaining.trim();

  // Build output
  let html = "";
  if (prefix) {
    html += `<p>${prefix}</p>`;
  }
  html += buildHtmlTableFromData(
    headerCells.length === colCount ? headerCells : headerCells.slice(0, colCount),
    dataRows
  );
  if (suffix) {
    html += `<p>${suffix}</p>`;
  }

  return html;
}

/**
 * Build HTML table from parsed multi-line markdown table lines.
 */
function buildMultiLineTable(lines: string[]): string {
  const parseRow = (line: string) =>
    line.split("|").slice(1, -1).map(c => c.trim());

  // Find separator line
  let sepIdx = -1;
  for (let j = 0; j < lines.length; j++) {
    if (/^\|[\s:|-]+\|$/.test(lines[j].trim())) {
      sepIdx = j;
      break;
    }
  }

  if (sepIdx === -1) {
    // No separator — treat first row as header
    sepIdx = 0;
  }

  const headers = parseRow(lines[sepIdx > 0 ? 0 : 0]);
  const dataRows = lines.slice(sepIdx + 1).map(parseRow);
  return buildHtmlTableFromData(headers, dataRows);
}

/**
 * Build an HTML table string from headers and data rows.
 */
function buildHtmlTableFromData(headers: string[], rows: string[][]): string {
  let html = '<table class="question-table"><thead><tr>';
  for (const h of headers) {
    html += `<th>${h}</th>`;
  }
  html += "</tr></thead><tbody>";
  for (const row of rows) {
    html += "<tr>";
    for (const cell of row) {
      html += `<td>${cell}</td>`;
    }
    html += "</tr>";
  }
  html += "</tbody></table>";
  return html;
}

/**
 * Wrap blank-line-separated blocks of plain text in <p> tags.
 *
 * markdownTablesToHtml already turns table blocks into <table> HTML;
 * everything else survives as bare text joined by newlines, which a
 * browser collapses to a single run with no visible paragraph breaks —
 * curriculum content authored as several paragraphs rendered as one
 * unbroken wall of text. This restores the break the author actually
 * wrote, without requiring authors to hand-write HTML in YAML.
 */
function wrapParagraphs(html: string): string {
  const blocks = html.split(/\n\s*\n/);
  return blocks
    .map((block) => {
      const trimmed = block.trim();
      if (!trimmed) return "";
      // Already-generated HTML (a table, or any literal markup an author
      // wrote directly) is left alone — only bare text gets wrapped.
      if (/^<(table|p|ul|ol|div|h[1-6])[\s>]/i.test(trimmed)) return trimmed;
      // Newlines are content structure, not HTML supplied by an author. Emit
      // the break ourselves after the text has been escaped so multi-line
      // worked examples remain readable without re-enabling raw markup.
      return `<p>${trimmed.replace(/\n/g, "<br />")}</p>`;
    })
    .filter(Boolean)
    .join("\n");
}

/**
 * Render plain-ASCII maths notation.
 *
 * The curricula were authored as plain text — `2x^2 + 5x + k`, `x^(1/2)`,
 * `sqrt[(x₂-x₁)^2 + (y₂-y₁)^2]`, `8sqrt5` — with no `$…$` delimiters, so
 * KaTeX's auto-render never sees them and learners were shown a literal
 * caret and the word "sqrt". Rather than re-author tens of thousands of
 * YAML lines, turn the forms actually in use into `<sup>` and `√` here.
 * Anything already inside a math delimiter is left to KaTeX. Runs on
 * escaped text, so an exponent can never introduce markup.
 */
const MATH_SPAN = /(\$\$[\s\S]+?\$\$|\$[^$\n]+?\$|\\\([\s\S]+?\\\)|\\\[[\s\S]+?\\\])/;
const CARET = /\^(?:\(([^()]+)\)|\{([^{}]+)\}|(-?\d+(?:\.\d+)?|[A-Za-z]|½|⅓|¼))/g;
// `sqrt[...]` is the authors' way of bracketing a long radicand; `sqrt(...)`,
// `sqrt320` and `8sqrt5` also occur. The lookbehind keeps a word such as
// "sqrtwo" (none exist, but instructors type freely) from being mangled.
const SQRT_BRACKET = /(?<![A-Za-z])sqrt\s*\[([^[\]]*)\]/g;
const SQRT_WORD = /(?<![A-Za-z])sqrt\s*(?=[(\dA-Za-zπ])/g;

function asciiMathToHtml(text: string): string {
  return text
    .split(MATH_SPAN)
    .map((part, i) =>
      i % 2 === 1
        ? part
        : part
            .replace(SQRT_BRACKET, "√($1)")
            .replace(SQRT_WORD, "√")
            .replace(CARET, (_m, paren, brace, bare) => `<sup>${paren ?? brace ?? bare}</sup>`),
    )
    .join("");
}

/** Escape plain text, then apply the table/paragraph/notation passes. */
function plainTextToHtml(content: string, inline: boolean): string {
  // API content is plain text, including instructor and AI output. Escape
  // it before generating our own table/paragraph markup.
  const escaped = content.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  const notated = asciiMathToHtml(escaped);
  return inline ? notated : wrapParagraphs(markdownTablesToHtml(notated));
}

/**
 * Convenience: render a raw string with math (sets innerHTML).
 * Use when the content comes from the API as a plain string with LaTeX.
 * Also converts markdown tables to HTML tables.
 */
export function MathText({
  content,
  className,
  inline = false,
}: {
  content: string;
  className?: string;
  /** Render as a <span> with no paragraph/table wrapping — for labels,
   *  tags and one-line descriptions that sit inside other markup. */
  inline?: boolean;
}) {
  const ref = useRef<HTMLElement>(null);

  useEffect(() => {
    if (ref.current) {
      ref.current.innerHTML = plainTextToHtml(content, inline);
      renderMathInElement(ref.current, {
        delimiters: DELIMITERS,
        throwOnError: false,
        trust: false,
      });
    }
  }, [content, inline]);

  return inline ? (
    <span ref={ref as RefObject<HTMLSpanElement>} className={className} />
  ) : (
    <div ref={ref as RefObject<HTMLDivElement>} className={className} />
  );
}
